import React from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useSettings } from "@/lib/settings";
import { LayoutDashboard, KeyRound, Files, LogOut, ExternalLink, Settings as SettingsIcon } from "lucide-react";

const linkBase = "flex items-center gap-3 px-4 py-2.5 rounded-md text-sm transition-all duration-200";
const linkInactive = "text-[var(--vault-text-muted)] hover:text-white hover:bg-white/5";
const linkActive = "bg-white/5 text-white border border-white/10";

export default function AdminLayout() {
  const navigate = useNavigate();
  const { settings } = useSettings();
  const email = localStorage.getItem("vault_email") || "admin";

  const logout = () => {
    localStorage.removeItem("vault_token");
    localStorage.removeItem("vault_email");
    navigate("/admin/login");
  };

  return (
    <div className="min-h-screen flex" style={{ background: "var(--vault-bg)" }} data-testid="admin-layout">
      <aside
        className="w-64 flex-shrink-0 border-r flex flex-col"
        style={{ borderColor: "var(--vault-border)", background: "var(--vault-surface)" }}
      >
        <div className="px-6 py-6 border-b" style={{ borderColor: "var(--vault-border)" }}>
          <div className="flex items-center gap-3">
            <img src={settings.logo_url} alt={settings.brand_name} className="w-9 h-9 object-contain" style={{ mixBlendMode: 'lighten' }} />
            <span className="font-display text-lg">{settings.brand_name.split(" ")[0]}</span>
          </div>
          <p className="overline text-[var(--vault-text-faint)] mt-3">Cellar Console</p>
        </div>

        <nav className="flex-1 p-4 space-y-1">
          <NavLink
            to="/admin/overview"
            data-testid="nav-overview"
            className={({ isActive }) => `${linkBase} ${isActive ? linkActive : linkInactive}`}
          >
            <LayoutDashboard className="w-4 h-4" />
            Overview
          </NavLink>
          <NavLink
            to="/admin/codes"
            data-testid="nav-codes"
            className={({ isActive }) => `${linkBase} ${isActive ? linkActive : linkInactive}`}
          >
            <KeyRound className="w-4 h-4" />
            Access Codes
          </NavLink>
          <NavLink
            to="/admin/files"
            data-testid="nav-files"
            className={({ isActive }) => `${linkBase} ${isActive ? linkActive : linkInactive}`}
          >
            <Files className="w-4 h-4" />
            Files
          </NavLink>
          <NavLink
            to="/admin/settings"
            data-testid="nav-settings"
            className={({ isActive }) => `${linkBase} ${isActive ? linkActive : linkInactive}`}
          >
            <SettingsIcon className="w-4 h-4" />
            Settings
          </NavLink>
        </nav>

        <div className="p-4 border-t space-y-2" style={{ borderColor: "var(--vault-border)" }}>
          <a
            href="/"
            target="_blank"
            rel="noreferrer"
            data-testid="open-portal"
            className={`${linkBase} ${linkInactive}`}
          >
            <ExternalLink className="w-4 h-4" />
            Open Portal
          </a>
          <div className="px-4 py-2">
            <p className="overline text-[var(--vault-text-faint)]">Signed in</p>
            <p className="text-sm truncate text-[var(--vault-text-muted)]" data-testid="admin-email">{email}</p>
          </div>
          <button
            onClick={logout}
            data-testid="logout-button"
            className={`${linkBase} ${linkInactive} w-full`}
          >
            <LogOut className="w-4 h-4" />
            Sign out
          </button>
        </div>
      </aside>

      <main className="flex-1 overflow-auto">
        <div className="p-6 sm:p-10 lg:p-12 max-w-7xl">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
